/**
 * Types for the member checkout flow (AR-290).
 *
 * The flow is front-end only for MVP: plan pricing, USPS verification, Stripe
 * and provisioning are stubbed in `services.ts` so every designed state can be
 * exercised without a backend.
 */

export const CHECKOUT_STEPS = [
  'login',
  'contact',
  'accelerator',
  'payment',
  'complete',
] as const;

export type CheckoutStepId = (typeof CHECKOUT_STEPS)[number];

export interface AddressDraft {
  street: string;
  unit: string;
  city: string;
  state: string;
  zip: string;
}

export interface LoginDraft {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
}

export interface ContactDraft {
  legalName: string;
  billing: AddressDraft;
  mailingSameAsBilling: boolean;
  mailing: AddressDraft;
  phone: string;
  agencyName: string;
  /** '' until the advisor answers. */
  hasAssociates: '' | 'yes' | 'no';
  associateCount: string;
  referredBy: string;
}

export interface AcceleratorDraft {
  added: boolean;
  /** Cohort ids — multiple dates may be selected. */
  cohortIds: string[];
  format: '' | 'virtual' | 'in_person';
}

export interface PaymentDraft {
  cardNumber: string;
  expiry: string;
  cvc: string;
  zip: string;
}

export interface AgreementDraft {
  termsAccepted: boolean;
  marketingOptIn: boolean;
}

export interface CheckoutDraft {
  step: CheckoutStepId;
  login: LoginDraft;
  contact: ContactDraft;
  accelerator: AcceleratorDraft;
  payment: PaymentDraft;
  agreement: AgreementDraft;
  /** Code applied to the order, if any. */
  appliedCoupon: string;
  /** ISO timestamp of the last save — drives the "resume" banner. */
  savedAt: string;
}

/** Field-level errors keyed by field path, e.g. `billing.zip`. */
export type FieldErrors = Record<string, string>;

export const emptyAddress = (): AddressDraft => ({
  street: '',
  unit: '',
  city: '',
  state: '',
  zip: '',
});

export const emptyDraft = (): CheckoutDraft => ({
  step: 'login',
  login: {
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  },
  contact: {
    legalName: '',
    billing: emptyAddress(),
    mailingSameAsBilling: true,
    mailing: emptyAddress(),
    phone: '',
    agencyName: '',
    hasAssociates: '',
    associateCount: '',
    referredBy: '',
  },
  accelerator: { added: false, cohortIds: [], format: '' },
  payment: { cardNumber: '', expiry: '', cvc: '', zip: '' },
  // Marketing consent is never pre-ticked: pre-selected consent isn't valid
  // consent under GDPR/ePrivacy. The "you'll miss out" warning does the nudging.
  agreement: { termsAccepted: false, marketingOptIn: false },
  appliedCoupon: '',
  savedAt: '',
});
